import { useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { LoginModal, openLoginModal } from '../auth';

/**
 * Gate a protected route behind authentication. On unauthenticated access we
 * push `?login=1` onto the current URL instead of redirecting to `/` so that
 * the LoginModal opens in place — after OAuth completes the callback returns
 * the user to this same URL (buildRedirectTo in LoginModal strips `login`).
 * Renders <LoginModal /> locally because protected routes aren't nested under
 * App (the only current mounter of the global modal).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading || user) return;
    openLoginModal(location, navigate, { replace: true });
  }, [loading, user, location, navigate]);

  if (loading) {
    return (
      <div className="require-auth-placeholder" role="status" aria-live="polite">
        불러오는 중…
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="require-auth-placeholder" role="status" aria-live="polite">
          로그인이 필요합니다.
        </div>
        <LoginModal />
      </>
    );
  }

  return <>{children}</>;
}
